import { z } from 'zod';
import { pageSchema, resourceKeySchema, timestampSchema } from './common';

/**
 * External integrations (architecture documentation section 11).
 *
 * A provider is described as data: which adapter drives it, what configuration
 * it needs, which credentials it holds, and what it is capable of. The
 * application depends on this internal shape, never on a provider's own schema
 * -- that separation (principle 5.3) is what lets two providers supply the same
 * kind of data, and what stops a provider's breaking change reaching the domain
 * model.
 */

export const INTEGRATION_PROVIDERS = ['http-endpoint', 'prometheus', 'webhook-source'] as const;
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];
export const integrationProviderSchema = z.enum(INTEGRATION_PROVIDERS);

/**
 * `error` and `paused` are different states on purpose. An integration that is
 * failing should keep being retried and stay visible as broken; one an operator
 * has paused must stop being called entirely (section 19.3).
 */
export const integrationStatusSchema = z.enum(['active', 'paused', 'error']);
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

/**
 * A provider identifier, not restricted to `INTEGRATION_PROVIDERS`.
 *
 * `Integration` is one shared resource for ops providers and cost providers
 * alike (integrations.service.ts explains why): the registry a provider is
 * actually resolved against -- one of the two `AdapterRegistry<TRow>`
 * instances `packages/integrations/src/registry.ts` builds, one per domain
 * -- is the real authority on which identifiers are valid, not a static enum
 * here.
 * `integrationProviderSchema` stays the narrower ops-only union below, used
 * only by `integrationConfigSchema`, which validates an ops provider's
 * settings shape specifically.
 */
export const providerIdentifierSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'must be lowercase alphanumeric with hyphens');

/* -------------------------------------------------------------------------- */
/* Per-provider configuration                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Server-side request forgery is the defining risk of a feature that fetches a
 * user-supplied URL (section 14.3). The schema rejects the obvious cases here;
 * the adapter re-checks the resolved address at call time, because DNS can
 * point a public hostname at a private one between validation and use.
 */
const externalUrlSchema = z
  .string()
  .url()
  .max(2048)
  .refine((value) => /^https?:\/\//i.test(value), { message: 'must be an http or https URL' })
  .refine((value) => extractHost(value) !== null, { message: 'must have a host' })
  .refine((value) => !isObviouslyInternalHost(extractHost(value) ?? ''), {
    message: 'must not target a loopback, link-local or private address',
  });

/**
 * Extracts the host without `URL`, which is not in this package's lib: these
 * schemas are shared with the browser, and pulling in Node or DOM types to get
 * one global would let environment-specific APIs leak into a package that must
 * run in both.
 */
function extractHost(value: string): string | null {
  const match = /^https?:\/\/(?:[^@/]*@)?([^/:?#]+)/i.exec(value);
  return match?.[1]?.toLowerCase() ?? null;
}

/**
 * A cheap first pass, not the real control. It rejects the obvious literals at
 * configuration time so an operator gets an immediate, comprehensible error.
 * The authoritative check is in the HTTP client, which resolves the hostname
 * immediately before connecting -- because a public name can be repointed at a
 * private address long after this schema accepted it.
 */
function isObviouslyInternalHost(host: string): boolean {
  return (
    host === 'localhost' ||
    host === '::1' ||
    host === '[::1]' ||
    host.endsWith('.localhost') ||
    host.endsWith('.internal') ||
    host.endsWith('.local') ||
    /^127\./.test(host) ||
    /^10\./.test(host) ||
    /^0\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^169\.254\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host)
  );
}

export const httpEndpointConfigSchema = z
  .object({
    url: externalUrlSchema,
    method: z.enum(['GET', 'HEAD']).default('GET'),
    /** Response codes treated as healthy. Anything else is a degraded reading. */
    expectedStatus: z.array(z.number().int().min(100).max(599)).min(1).default([200]),
    /** Optional substring the body must contain for the check to pass. */
    expectedBodyContains: z.string().max(200).optional(),
    timeoutMs: z.number().int().min(500).max(30_000).default(5_000),
    /** Above this, the service is reported degraded rather than operational. */
    degradedAboveMs: z.number().int().min(1).max(60_000).default(2_000),
    headerName: z.string().max(64).optional(),
  })
  .strict();
export type HttpEndpointConfig = z.infer<typeof httpEndpointConfigSchema>;

export const prometheusConfigSchema = z
  .object({
    baseUrl: externalUrlSchema,
    /** Instant-vector query returning one sample per monitored service. */
    query: z.string().min(1).max(2000),
    /** Label carrying the service key, so results map onto the inventory. */
    serviceLabel: z.string().min(1).max(64).default('infrastructure_asset'),
    timeoutMs: z.number().int().min(500).max(30_000).default(10_000),
    degradedAboveMs: z.number().int().min(1).max(600_000).default(2_000),
  })
  .strict();
export type PrometheusConfig = z.infer<typeof prometheusConfigSchema>;

export const webhookSourceConfigSchema = z
  .object({
    /** Header carrying the signature, e.g. X-Hub-Signature-256. */
    signatureHeader: z.string().min(1).max(64).default('x-signature-256'),
    signatureScheme: z.enum(['hmac-sha256', 'hmac-sha256-prefixed']).default('hmac-sha256'),
    /** Header carrying the provider's own event id, used to deduplicate. */
    eventIdHeader: z.string().min(1).max(64).default('x-event-id'),
    timestampHeader: z.string().max(64).optional(),
    /** Deliveries older than this are refused as replays. */
    toleranceSeconds: z.number().int().min(30).max(3600).default(300),
  })
  .strict();
export type WebhookSourceConfig = z.infer<typeof webhookSourceConfigSchema>;

export const integrationConfigSchema = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('http-endpoint'), settings: httpEndpointConfigSchema }),
  z.object({ provider: z.literal('prometheus'), settings: prometheusConfigSchema }),
  z.object({ provider: z.literal('webhook-source'), settings: webhookSourceConfigSchema }),
]);
export type IntegrationConfig = z.infer<typeof integrationConfigSchema>;

/* -------------------------------------------------------------------------- */
/* Credentials                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Credentials are write-only across the API boundary. They can be set and
 * replaced, never read back -- a stored secret that an administration screen
 * can display is a stored secret that ends up in a screenshot, a support ticket
 * and a browser cache (section 14.4).
 */
export const integrationCredentialSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('bearer'), token: z.string().min(1).max(4096) }),
  z.object({
    kind: z.literal('basic'),
    username: z.string().min(1).max(256),
    password: z.string().min(1).max(1024),
  }),
  z.object({
    kind: z.literal('header'),
    name: z.string().min(1).max(64),
    value: z.string().min(1).max(4096),
  }),
  z.object({ kind: z.literal('hmac'), secret: z.string().min(16).max(1024) }),
  /**
   * The Entra ID (Azure AD) client-credentials grant: an application secret
   * exchanged for a bearer token at call time, rather than a static header.
   * Unlike every other kind, this one is not something `applyCredential` can
   * attach directly -- token acquisition and caching is the adapter's own job
   * (packages/integrations/src/adapters/ms-graph-excel.ts), because a token
   * expires and a static header never does.
   */
  z.object({
    kind: z.literal('oauth2_client_credentials'),
    tenantId: z.string().min(1).max(128),
    clientId: z.string().min(1).max(128),
    clientSecret: z.string().min(1).max(4096),
  }),
]);
export type IntegrationCredential = z.infer<typeof integrationCredentialSchema>;

export const credentialKindSchema = z.enum([
  'none',
  'bearer',
  'basic',
  'header',
  'hmac',
  'oauth2_client_credentials',
]);
export type CredentialKind = z.infer<typeof credentialKindSchema>;

/* -------------------------------------------------------------------------- */
/* Resource shape                                                              */
/* -------------------------------------------------------------------------- */

export const integrationSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  provider: providerIdentifierSchema,
  status: integrationStatusSchema,
  /** Non-secret settings only; credentials never appear here. */
  settings: z.record(z.string(), z.unknown()),
  credentialKind: credentialKindSchema,
  /** True when a credential is stored, so the UI can offer "replace". */
  hasCredential: z.boolean(),
  syncIntervalSeconds: z.number().int().nullable(),
  lastSyncAt: timestampSchema.nullable(),
  lastSuccessAt: timestampSchema.nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorSummary: z.string().nullable(),
  consecutiveFailures: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Integration = z.infer<typeof integrationSchema>;

export const integrationPageSchema = pageSchema(integrationSchema);

export const createIntegrationSchema = z
  .object({
    key: resourceKeySchema,
    name: z.string().min(1).max(200),
    provider: providerIdentifierSchema,
    settings: z.record(z.string(), z.unknown()),
    credential: integrationCredentialSchema.default({ kind: 'none' }),
    syncIntervalSeconds: z.number().int().min(30).max(86_400).nullish(),
  })
  .strict();
export type CreateIntegration = z.infer<typeof createIntegrationSchema>;

export const updateIntegrationSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    /** Omitted leaves the stored credential untouched; present replaces it. */
    credential: integrationCredentialSchema.optional(),
    status: z.enum(['active', 'paused']).optional(),
    syncIntervalSeconds: z.number().int().min(30).max(86_400).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateIntegration = z.infer<typeof updateIntegrationSchema>;

export const integrationListQuerySchema = z.object({
  provider: providerIdentifierSchema.optional(),
  status: integrationStatusSchema.optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/**
 * One registered provider, as offered to a create/edit form -- the same
 * extension-point shape `catalogEntryWireSchema` (components.ts) already
 * uses for dashboard components: a stable identifier, localization keys, and
 * a settings shape converted to JSON Schema so the browser generates the
 * settings form instead of one being hand-written per provider (section
 * 8.3). Credentials stay hand-built (they're the security-sensitive half,
 * and there are only six kinds), but `supportedCredentials` still comes from
 * here so the form only offers the kinds this provider actually accepts.
 */
export const integrationProviderCatalogEntrySchema = z.object({
  provider: providerIdentifierSchema,
  titleKey: z.string(),
  descriptionKey: z.string(),
  configJsonSchema: z.record(z.string(), z.unknown()),
  supportedCredentials: z.array(credentialKindSchema),
  capabilities: z.object({ sync: z.boolean(), webhook: z.boolean() }),
});
export type IntegrationProviderCatalogEntry = z.infer<typeof integrationProviderCatalogEntrySchema>;

/**
 * Result of a connection test. Diagnostics are sanitized before they reach an
 * operator's screen: a provider's error body can echo the credential that was
 * just sent to it (section 11.2).
 */
export const integrationTestResultSchema = z.object({
  ok: z.boolean(),
  durationMs: z.number().int().nonnegative(),
  errorCode: z.string().nullable(),
  /** Redacted, truncated, and safe to display. */
  detail: z.string().nullable(),
  observations: z.number().int().nonnegative(),
});
export type IntegrationTestResult = z.infer<typeof integrationTestResultSchema>;

/** Per-provider health, for the operations view (section 11.2). */
export const integrationHealthSchema = z.object({
  integrationId: z.string().uuid(),
  key: z.string(),
  provider: providerIdentifierSchema,
  status: integrationStatusSchema,
  /** Open means the adapter is shedding calls after repeated failures. */
  circuitOpen: z.boolean(),
  consecutiveFailures: z.number().int().nonnegative(),
  lastSuccessAt: timestampSchema.nullable(),
  lastErrorCode: z.string().nullable(),
  p95LatencyMs: z.number().nullable(),
  successRate: z.number().min(0).max(1).nullable(),
});
export type IntegrationHealth = z.infer<typeof integrationHealthSchema>;

/* -------------------------------------------------------------------------- */
/* Webhooks                                                                    */
/* -------------------------------------------------------------------------- */

export const webhookDeliveryStatusSchema = z.enum([
  'received',
  'processed',
  'rejected',
  'duplicate',
  'failed',
]);

export const webhookDeliverySchema = z.object({
  id: z.string().uuid(),
  integrationId: z.string().uuid(),
  providerEventId: z.string().nullable(),
  eventType: z.string().nullable(),
  status: webhookDeliveryStatusSchema,
  signatureValid: z.boolean(),
  receivedAt: timestampSchema,
  processedAt: timestampSchema.nullable(),
  errorCode: z.string().nullable(),
});
export type WebhookDelivery = z.infer<typeof webhookDeliverySchema>;

export const webhookDeliveryPageSchema = pageSchema(webhookDeliverySchema);
